/**
 * Session Expiration Handler
 * 
 * Handles session expiration scenarios:
 * 1. OLESessionHasExpired cookie detection (server-set cookie indicating session timeout)
 * 2. JWT expiration (401 responses from API calls)
 * 
 * Uses singleton pattern to ensure only one dialog/action is triggered at a time,
 * preventing race conditions when multiple API calls fail simultaneously with 401.
 * 
 * ## Key Scenario: Stale Credentials on App Load
 * 
 * When the app loads with expired credentials stored in localStorage:
 * 1. Auth context finds stored user, sets `user` state
 * 2. App makes authenticated API calls (getmenuitems, getprofiles, etc.)
 * 3. These calls fail with 401 (token expired on server)
 * 4. Axios interceptor catches 401 and calls handleJWTExpiration()
 * 5. This handler clears credentials and redirects to login
 * 
 * Without this handler, the app would get stuck in a loop:
 * - Stored user exists → try to navigate → 401 → error logged but not handled
 * - Page refreshes → stored user still exists → repeat
 * 
 * The `isDialogShowing` flag prevents multiple 401s (which fire in rapid succession)
 * from triggering multiple dialogs or redirects.
 */

import { Dialog, DialogUtility } from '@syncfusion/ej2-popups';
import { environment } from '@/config/environment';
import { localStorageVariables } from '@/types/localStorageVariables';
import { performLogout, clearAuthWithoutContext } from './logout';
import { getCookie, deleteCookie } from '@/lib/utils/cookie';
import { i18n } from 'yardi-react-translation/translationService';
import type { NavigateFunction } from 'react-router-dom';

class SessionExpirationHandler {
    private static instance: SessionExpirationHandler;
    private currentDialog: Dialog | null = null;
    private isDialogShowing: boolean = false;

    // Stored logout handlers for use in JWT expiration
    private storedNavigate: NavigateFunction | null = null;
    private storedAuthLogout: (() => Promise<void>) | null = null;
    private storedProperty: string | undefined = undefined;

    private constructor() { }

    public static getInstance(): SessionExpirationHandler {
        if (!SessionExpirationHandler.instance) {
            SessionExpirationHandler.instance = new SessionExpirationHandler();
        }
        return SessionExpirationHandler.instance;
    }

    /**
     * Store logout handlers for use when JWT expiration is detected from API interceptors
     * @param navigate - React Router navigate function
     * @param authLogout - Auth context logout function
     * @param property - Current property from URL params
     */
    public setLogoutHandlers(
        navigate: NavigateFunction,
        authLogout: () => Promise<void>,
        property?: string
    ): void {
        this.storedNavigate = navigate;
        this.storedAuthLogout = authLogout;
        this.storedProperty = property;
    }

    /**
     * Handle JWT expiration (401 response) - show alert and logout
     * Called from axios interceptors when 401 status code is received.
     * 
     * This is the centralized handler for all 401 errors in the app.
     * Individual API callers (like loginService.handleAfterLogin) don't need
     * to handle 401 specially - this interceptor-based approach ensures
     * consistent handling regardless of where the 401 originates.
     */
    public handleJWTExpiration(): void {
        // Race condition protection: Multiple API calls can fail with 401 simultaneously
        // (e.g., getmenuitems, getprofiles, etc. all fire on app load).
        // Only the first 401 should trigger action; subsequent ones are ignored.
        if (this.isDialogShowing) {
            return;
        }

        // Check if logout handlers are available (set by useSessionHeartbeat hook)
        if (!this.storedNavigate || !this.storedAuthLogout) {
            // FALLBACK PATH: This executes when 401 occurs during early app initialization,
            // before useSessionHeartbeat has run to set up the logout handlers.
            // 
            // Scenario: App loads with stale credentials in localStorage
            // 1. Auth context reads stored user → sets user state
            // 2. Components make API calls before useSessionHeartbeat mounts
            // 3. API calls return 401 → this handler is called
            // 4. But logout handlers aren't set up yet!
            // 
            // Solution: Use clearAuthWithoutContext() which clears credentials
            // directly without needing React context, then hard redirect to login.
            // This breaks the stale credential loop.
            console.warn('🔴 handleJWTExpiration: Logout handlers not available (early init), clearing credentials and redirecting');
            clearAuthWithoutContext();
            
            // Hard redirect to login page (can't use React Router navigate here)
            const loginPath = `/onlineleasingstudent/content3/access/${localStorage.getItem(localStorageVariables.sitename)}/login`;
            window.location.href = loginPath;
            return;
        }

        // Mark dialog as showing
        this.isDialogShowing = true;

        // Show alert dialog with only OK button
        this.currentDialog = DialogUtility.alert({
            title: i18n.t('common.sessionExpired'),
            content: i18n.t('common.sessionExpiredMessage'),
            closeOnEscape: false,
            showCloseIcon: false,
            okButton: {
                text: i18n.t('common.ok'),
                click: () => {
                    this.currentDialog?.close();
                    this.isDialogShowing = false;
                    // Perform logout - use stored property, fallback to sitename from localStorage
                    const propertyForLogout = this.storedProperty || localStorage.getItem(localStorageVariables.sitename) || undefined;
                    performLogout({
                        navigate: this.storedNavigate!,
                        authLogout: this.storedAuthLogout!,
                        property: propertyForLogout
                    });
                }
            }
        });
    }

    /**
     * Check if session has expired by looking for the OLESessionHasExpired cookie
     * @returns true if session has expired
     */
    public checkSessionExpired(): boolean {
        const cookieValue = getCookie(localStorageVariables.SCOLESessionHasExpiredCookieKey);
        return cookieValue === '1';
    }

    /**
     * Handle session expiration - show dialog if not already showing
     * @param navigate - React Router navigate function
     * @param authLogout - Auth context logout function
     * @param property - Current property from URL params
     */
    public async handleSessionExpiration(
        navigate: NavigateFunction,
        authLogout: () => Promise<void>,
        property?: string
    ): Promise<void> {
        // Check if dialog is already showing
        if (this.isDialogShowing) {
            return;
        }

        // Check if session has expired
        if (!this.checkSessionExpired()) {
            return;
        }

        // Mark dialog as showing
        this.isDialogShowing = true;

        // Delete the cookie to prevent multiple dialogs
        deleteCookie(localStorageVariables.SCOLESessionHasExpiredCookieKey);

        // Clear liui cookie used by OmniChannel Chat IQ
        deleteCookie('liui', '/');

        // Show the dialog
        this.currentDialog = DialogUtility.confirm({
            title: i18n.t('common.logout'),
            content: i18n.t('common.logoutConfirmation'),
            closeOnEscape: false,
            showCloseIcon: false,
            okButton: {
                text: i18n.t('common.continuesession'),
                click: async () => {
                    this.currentDialog?.hide();
                    try {
                        const restoreResponse = await fetch(`${environment.apiPrefix.rootPath}session/restore`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            credentials: 'include',
                            body: JSON.stringify({
                                EncPropertyId: localStorage.getItem(localStorageVariables.EncPropertyId),
                                EncProspectId: localStorage.getItem(localStorageVariables.EncProspectId),
                                EncPmUserExId: localStorage.getItem(localStorageVariables.EncPmUserExId)
                            })
                        });

                        if (!restoreResponse.ok) {
                            console.log('Session restore failed on server');
                            this.currentDialog = DialogUtility.alert({
                                title: i18n.t('common.error'),
                                content: i18n.t('common.couldNotRestoreSession'),
                                closeOnEscape: false,
                                showCloseIcon: false,
                                okButton: {
                                    text: i18n.t('common.ok'),
                                    click: () => {
                                        this.currentDialog?.close();
                                        this.isDialogShowing = false;
                                        performLogout({
                                            navigate,
                                            authLogout,
                                            property
                                        });
                                    }
                                }
                            });
                        } else {
                            // Session restored successfully
                            this.isDialogShowing = false;
                        }
                    } catch (error) {
                        console.log(`Session restore failed: ${error}`);
                        this.isDialogShowing = false;
                        // Show error and logout
                        this.currentDialog = DialogUtility.alert({
                            title: i18n.t('common.error'),
                            content: i18n.t('common.couldNotRestoreSession'),
                            closeOnEscape: false,
                            showCloseIcon: false,
                            okButton: {
                                text: i18n.t('common.ok'),
                                click: () => {
                                    this.currentDialog?.close();
                                    performLogout({
                                        navigate,
                                        authLogout,
                                        property
                                    });
                                }
                            }
                        });
                    }
                }
            },
            cancelButton: {
                text: i18n.t('common.logout'),
                click: () => {
                    this.currentDialog?.hide();
                    this.isDialogShowing = false;
                    // Perform comprehensive logout
                    performLogout({
                        navigate,
                        authLogout,
                        property
                    });
                }
            },
        });
    }

    /**
     * Reset the dialog state (used for cleanup)
     */
    public reset(): void {
        this.isDialogShowing = false;
        if (this.currentDialog) {
            this.currentDialog.close();
            this.currentDialog = null;
        }
    }
}

export const sessionExpirationHandler = SessionExpirationHandler.getInstance();
